/**
 * 从 pdf / 文本 / Word 文档中读取正文，作为历史菜单预览内容
 */
import JSZip from 'jszip'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import { readMenuTextPayload } from './menuPdfPayload'
import { buildHistoryFileStem, parseHistoryFileName } from './historyFileName'

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || './'}pdf.worker.min.js`

const TEXT_EXT = ['txt', 'text', 'md', 'csv']
const DOC_EXT = ['docx', 'doc', 'rtf', 'wps']
const PDF_EXT = ['pdf']

export function isHistoryImportFile(fileName) {
  const ext = getExt(fileName)
  return TEXT_EXT.includes(ext) || DOC_EXT.includes(ext) || PDF_EXT.includes(ext) || ext === 'zip'
}

function getExt(fileName) {
  const m = String(fileName || '').toLowerCase().match(/\.([^.]+)$/)
  return m ? m[1] : ''
}

function decodeText(buffer) {
  const bytes = new Uint8Array(buffer)
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(buffer)
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(buffer)
  }
  return new TextDecoder('utf-8').decode(buffer)
}

async function extractPdfText(arrayBuffer) {
  const embedded = readMenuTextPayload(arrayBuffer)
  if (embedded) return embedded

  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
    disableWorker: true,
    useSystemFonts: true
  })
  const pdf = await loadingTask.promise
  const pages = []
  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent({ includeMarkedContent: true })
    let line = ''
    let lastY = null
    const chunks = []
    content.items.forEach((item) => {
      if (!item || item.str == null) return
      const y = item.transform ? item.transform[5] : 0
      const str = String(item.str)
      if (item.hasEOL) {
        chunks.push(line + str)
        line = ''
        lastY = null
        return
      }
      if (lastY != null && Math.abs(y - lastY) > 4) {
        chunks.push(line)
        line = str
      } else {
        line += str
      }
      lastY = y
    })
    if (line) chunks.push(line)
    pages.push(chunks.join('\n'))
  }
  let text = pages.join('\n\n').trim()
  if (text) return text
  try {
    const meta = await pdf.getMetadata()
    const info = (meta && meta.info) || {}
    text = String(info.Subject || info.Title || '').trim()
  } catch (e) {
    text = ''
  }
  return text
}

async function extractDocxText(arrayBuffer) {
  const zip = await JSZip.loadAsync(arrayBuffer)
  const file = zip.file('word/document.xml')
  if (!file) {
    throw new Error('无法读取 Word 文档正文')
  }
  const xml = await file.async('string')
  return xml
    .split(/<w:p[\s>]/)
    .map((part) => {
      const texts = []
      const re = /<w:t[^>]*>([^<]*)<\/w:t>/g
      let m = re.exec(part)
      while (m) {
        texts.push(m[1]
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'"))
        m = re.exec(part)
      }
      return texts.join('')
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function extractRtfText(raw) {
  return String(raw || '')
    .replace(/\\par[d]?/g, '\n')
    .replace(/\\tab/g, '\t')
    .replace(/\\'[0-9a-fA-F]{2}/g, '')
    .replace(/\\[a-zA-Z]+\d* ?/g, '')
    .replace(/[{}]/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function isOleCompound(buffer) {
  const b = new Uint8Array(buffer)
  return b.length >= 8
    && b[0] === 0xd0
    && b[1] === 0xcf
    && b[2] === 0x11
    && b[3] === 0xe0
}

export async function readHistoryFileContent(file) {
  const ext = getExt(file.name)
  const buffer = await file.arrayBuffer()
  if (PDF_EXT.includes(ext)) {
    const text = await extractPdfText(buffer)
    if (!text) throw new Error('未能从 PDF 中识别出文字')
    return text
  }
  if (ext === 'docx') {
    return extractDocxText(buffer)
  }
  if (ext === 'doc' || ext === 'wps') {
    if (isOleCompound(buffer)) {
      throw new Error('暂不支持旧版 .doc / .wps，请另存为 .docx、.txt 或 .pdf')
    }
    return extractDocxText(buffer)
  }
  if (ext === 'rtf') {
    return extractRtfText(decodeText(buffer))
  }
  if (TEXT_EXT.includes(ext)) {
    return decodeText(buffer).replace(/^\uFEFF/, '').trim()
  }
  throw new Error('不支持的文件类型，请使用 PDF、文本或 Word 文档')
}

export async function expandImportFiles(files) {
  const list = []
  for (let i = 0; i < (files || []).length; i += 1) {
    const file = files[i]
    const ext = getExt(file.name)
    if (ext !== 'zip') {
      list.push(file)
      continue
    }
    const zipParsed = parseHistoryFileName(file.name)
    const zip = await JSZip.loadAsync(await file.arrayBuffer())
    const names = Object.keys(zip.files)
    const extracted = []
    for (let j = 0; j < names.length; j += 1) {
      const entry = zip.files[names[j]]
      if (!entry || entry.dir) continue
      const base = String(names[j]).replace(/^.*[\\/]/, '')
      if (!isHistoryImportFile(base) || getExt(base) === 'zip') continue
      const bytes = await entry.async('uint8array')
      extracted.push({ bytes, base })
    }
    extracted.forEach((item) => {
      let outName = item.base
      if (zipParsed && extracted.length === 1) {
        const innerExt = getExt(item.base) || 'pdf'
        outName = `${buildHistoryFileStem(zipParsed.perCapita, zipParsed.guestCount)}.${innerExt}`
      }
      list.push(new File([item.bytes], outName, { type: 'application/octet-stream' }))
    })
  }
  return list
}
